import React, { useState, useEffect, useCallback } from "react";
import laraIcon from '../../images/vlara-logo.svg';

const API_URL = "https://lara-backend.lusep.fi/data/reservations/efin.json";

const Lara = ({ map }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [reservations, setReservations] = useState([]);

  const fetchReservations = useCallback(async () => {
    if (!map || isLoading) return;

    setIsLoading(true);
    try {
      const res = await fetch(API_URL);
      const data = await res.json();
      setReservations(data.filter(r => r.areas && r.areas.length > 0));
    } catch (err) {
      console.error("Failed to fetch LARA reservations:", err);
    } finally {
      setIsLoading(false);
    }
  }, [map, isLoading]);

  const toggleVisibility = useCallback(() => {
    const newVisible = !isVisible;
    setIsVisible(newVisible);

    if (newVisible && reservations.length === 0) {
      fetchReservations();
    }
  }, [isVisible, reservations, fetchReservations]);

  useEffect(() => {
    if (!map) return;

    const addedLayers = []; // store both fill and outline
    const addedSources = new Set();

    if (isVisible && reservations.length > 0) {
      reservations.forEach(reservation => {
        reservation.areas.forEach(area => {
          const sourceId = `lara-${reservation.reservation_id}-${area.area_id}`;
          const fillLayerId = sourceId;
          const outlineLayerId = `${sourceId}-outline`;

          if (!map.getSource(sourceId)) {
            map.addSource(sourceId, {
              type: "geojson",
              data: {
                type: "Feature",
                geometry: { type: "Polygon", coordinates: area.coordinates },
                properties: { reservation_id: reservation.reservation_id, status: reservation.status }
              }
            });
            addedSources.add(sourceId);
          }

          if (!map.getLayer(fillLayerId)) {
            map.addLayer({
              id: fillLayerId,
              type: "fill",
              source: sourceId,
              paint: {
                "fill-color":
                  reservation.status === "active" ? "red" :
                  reservation.status === "pending" ? "orange" :
                  reservation.status === "reserved" ? "#0492c2" : "gray",
                "fill-opacity": 0.4
              }
            });
            addedLayers.push(fillLayerId);
          }

          if (!map.getLayer(outlineLayerId)) {
            map.addLayer({
              id: outlineLayerId,
              type: "line",
              source: sourceId,
              paint: {
                "line-color":
                  reservation.status === "active" ? "red" :
                  reservation.status === "pending" ? "orange" :
                  reservation.status === "reserved" ? "#0492c2" : "black",
                "line-width": 2
              }
            });
            addedLayers.push(outlineLayerId);
          }
        });
      });
    }

    return () => {
      // First remove all layers
      addedLayers.forEach(layerId => {
        if (map.getLayer(layerId)) map.removeLayer(layerId);
      });

      // Then remove sources
      addedSources.forEach(sourceId => {
        if (map.getSource(sourceId)) map.removeSource(sourceId);
      });
    };
  }, [map, isVisible, reservations]);

  return (
    <button
      data-tooltip-id="map-buttons-tooltip"
      data-tooltip-content="Airspace reservations"
      className={`w-8 h-8 ${isVisible ? "bg-gray-600" : "bg-gray-400"} text-white flex items-center justify-center`}
      onClick={toggleVisibility}
      disabled={isLoading}
    >
      <img className="w-5" src={laraIcon} alt="Lara" />
    </button>
  );
};

export default Lara;
